<template>
    <ConfirmActionModal
        class="auction-confirm-create-bid"
        title="Подтвердите ставку"
        confirm-label="Подтвердить ставку"
        :on-confirm="onConfirmBid"
    >
        <div class="auction-confirm-create-bid__bid">
            <div class="auction-confirm-create-bid__bid-title">Ваша ставка</div>
            <div class="auction-confirm-create-bid__bid-value">
                {{ bidValue ? numberFormat(bidValue): 0 }}₽
            </div>
            <span v-if="errors?.bid_value" class="form-field__error">
                {{ errors.bid_value.join() }}
            </span>
        </div>

        <div class="entity-confirm-action">
            <div class="entity-confirm-action__title">Дилер, от которого делается ставка</div>
            <div class="entity">
                <div class="entity__title">{{ currentUser.dealer_name }}</div>
                <div class="entity__list">
                    <div class="entity__list-item">
                        <div class="entity__list-label">Город:</div>
                        <div class="entity__list-value">{{ currentUser.dealer_city_name }}</div>
                    </div>
                    <div class="entity__list-item">
                        <div class="entity__list-label">Код дилера:</div>
                        <div class="entity__list-value">{{ currentUser.dealer_sap_code }}</div>
                    </div>
                    <div
                        v-if="currentUser.holding_name"
                        class="entity__list-item"
                    >
                        <div class="entity__list-label">Холдинг:</div>
                        <div class="entity__list-value">{{ currentUser.holding_name }}</div>
                    </div>
                </div>
            </div>
        </div>

        <UICheckbox
            v-model="formRulesAccepted"
            :error-message="errors.auction_rules_accepted?.join() ?? ''"
        >
            С <RulesLink>правилами аукциона</RulesLink> ознакомлен. В случае победы, выкуп автомобиля по заявленной стоимости гарантируем.
        </UICheckbox>
    </ConfirmActionModal>
</template>

<script lang="ts" setup>
    import { computed, ref } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { AuthKey } from '@/injection/keys.ts';
    import Auth from '@/classes/Auth.ts';

    import { AuctionBidCreateForm, AuctionBidCreateFormErrors } from '@/types/auction.type.ts';
    import { CurrentUser } from '@/types/user.type.ts';

    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';

    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UICheckbox from '@/components/ui/UICheckbox/UICheckbox.vue';
    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';
    import RulesLink from '@/components/shared/RulesLink/RulesLink.vue';

    const { auctionId, bidValue } = defineProps({
        auctionId: {
            type: Number,
            required: true,
        },
        bidValue: {
            type: Number,
            required: true,
        },
    });

    const emit = defineEmits<{
        (e: 'confirmed'): void,
    }>();

    const $Auth: Auth = injectStrict<Auth>(AuthKey);

    const { createAuctionBid } = useAuctionRepository();

    const form = ref<AuctionBidCreateForm>({
        bid_value: bidValue,
        auction_rules_accepted: 0,
    });
    const errors = ref<AuctionBidCreateFormErrors>({});

    const formRulesAccepted = computed<boolean>({
        get: () => form.value.auction_rules_accepted === 1,
        set: (value) => {
            form.value.auction_rules_accepted = value ? 1 : 0;
        },
    });

    const currentUser: CurrentUser = $Auth.getCurrentUser();

    async function onConfirmBid(): Promise<AuctionBidCreateFormErrors | undefined> {
        errors.value = {};

        try {
            await createAuctionBid(auctionId, form.value);
        } catch (error) {
            errors.value = error as AuctionBidCreateFormErrors;

            return errors.value;
        }

        emit('confirmed');
    }
</script>

<style scoped lang="scss">
    @use './AuctionConfirmCreateBid.scss' as *;
</style>
